import * as React from 'react';
import {
  createChangeEvent,
  CustomTarget,
  SimulatedChangeEvent
} from '../../../hooks/useForm';
import { FComponent } from '../../../types/common';
import { classNames } from '../../../utils';
import { FieldErrors } from './FieldErrors';

export interface SliderTarget extends CustomTarget {
  name: string;
  type: 'slider';
  value: string | undefined;
}

type SliderProps = {
  name: string;
  label?: string;
  // onChangeStable: (key: string, event: React.ChangeEvent<HTMLInputElement>) => void;
  onChange: (event: SimulatedChangeEvent<SliderTarget>) => void;
  min: number;
  max: number;
  defaultValue?: number;
  ariaValueText?: string;
  ariaName: string;
  step: number;
  value: number;
  markers?: [string, string];
  visited?: boolean;
  errors?: string[];
  disabled?: boolean;
  className?: string;
  showSelection?: boolean;
};

const Slider: FComponent<SliderProps> = ({
  name,
  label,
  onChange,
  value,
  min,
  max,
  defaultValue,
  ariaName,
  ariaValueText,
  step,
  markers,
  visited,
  errors,
  disabled = false,
  showSelection = false,
  className = 'default-slider'
}) => {
  if (value === undefined && defaultValue === undefined) {
    throw new Error('Slider value is undefined and no default value is set');
  }
  const [inputValue, setInputValue] = React.useState(
    isNaN(value) ? '' : value.toString()
  );
  const handleSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.persist();
    setInputValue(event.target.value);
    onChange(
      createChangeEvent({
        type: 'slider',
        name,
        value: event.target.value
      })
    );
  };
  const handleManualChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.persist();
    setInputValue(event.target.value);
    event.target.name = name;
    if (!isValidNumber(event.target.value)) {
      event.target.value = undefined;
    }
    onChange(
      createChangeEvent({
        type: 'slider',
        name,
        value: event.target.value
      })
    );
  };
  const isValidNumber = React.useCallback(
    (n: number | string) => {
      let numericValue: number;
      if (typeof n === 'string') {
        if (/^-?[0-9]+$/.test(n)) {
          numericValue = +n;
        }
      } else if (typeof n === 'number') {
        numericValue = n;
      } else {
        return false;
      }
      return !isNaN(numericValue) && +n >= min && +n <= max;
    },
    [min, max]
  );

  const [leftMarker, rightMarker] = markers ?? [undefined, undefined];
  const inputErrors = isValidNumber(inputValue)
    ? []
    : [`Enter a valid number between ${min} and ${max}`];

  const currentValue = isValidNumber(value) ? value : 0;
  const ariaValue = `${ariaName} set to value ${currentValue} ${
    ariaValueText ? `representing ${ariaValueText}` : ''
  }`;

  const allErrors = [...(errors ?? []), ...inputErrors];

  return (
    <div className={classNames('field-container', className)}>
      <label
        id="input-label"
        htmlFor={name}
        className={classNames('input-label', label ? '' : 'sr-only')}>
        {label || ariaName}
      </label>
      <div className="slider-container">
        <div className="slider-contents">
          <input
            type="range"
            name={name}
            min={min}
            max={max}
            step={step}
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={currentValue}
            aria-valuetext={ariaValue}
            aria-labelledby="input-label"
            value={value === undefined || !isValidNumber(value) ? defaultValue : value}
            onChange={handleSliderChange}
            onClick={() =>
              [NaN, undefined].includes(value)
                ? handleSliderChange(createFakeSliderEvent(name, defaultValue))
                : (): null => null
            }
            disabled={disabled}
          />
          {markers ? (
            <div className="markers">
              {leftMarker ? <div className="marker-min">{leftMarker}</div> : null}
              {rightMarker ? <div className="marker-min">{rightMarker}</div> : null}
            </div>
          ) : null}
        </div>
        <input
          id={name + 'ManualEntry'}
          type="text"
          name={name + 'ManualEntry'}
          onChange={handleManualChange}
          value={inputValue}
          className="slider-text-input with-focus-ring"
          aria-label={'manual entry for ' + label}
          disabled={disabled}
        />
      </div>
      <FieldErrors className="slider-field-errors" visited={visited} errors={allErrors} />
      {showSelection ? <div>Selection: {ariaValueText}</div> : null}
    </div>
  );
};

const createFakeSliderEvent = (name: string, value: number) =>
  ({
    persist: (): null => null,
    target: { name, value: value.toString() }
  }) as React.ChangeEvent<HTMLInputElement>;

export { Slider };
